import React, { useEffect, useMemo, useState } from "react";
import api from "../lib/api";
import { useCurrency } from "../context/CurrencyContext";
import { useTheme } from "../context/ThemeContext";
import { useLanguage } from "../context/LanguageContext";
import { formatMoney, MIN_TRADES_FOR_INSIGHT } from "../lib/constants";
import { DASHBOARD_TIPS as TIP } from "../lib/dashboardTips";
import { KpiCard, MetricCard, ChartTooltip } from "../components/dashboard/Cards";
import DashboardEmptyState from "../components/dashboard/DashboardEmptyState";
import DataConfidencePill from "../components/DataConfidencePill";
import BehavioralInsightsDrawer from "../components/dashboard/BehavioralInsightsDrawer";
import BuildingProfileNotice from "../components/BuildingProfileNotice";
import MetricInfoTooltip from "../components/MetricInfoTooltip";
import ThesisGateBanner from "../components/coach/ThesisGateBanner";
import DayZeroChat from "../components/coach/DayZeroChat";
import {
  TrendingUp, TrendingDown, Target, Activity, ArrowUpRight, Scale, CalendarDays,
  Zap, Scale3D, ChevronsDown, Flame, Trophy,
} from "lucide-react";
import { Link } from "react-router-dom";
import {
  ResponsiveContainer, AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip,
} from "recharts";
import CalendarHeatmap from "../components/CalendarHeatmap";
import BreakdownWidget from "../components/BreakdownWidget";
import PatternOfTheWeek from "../components/PatternOfTheWeek";
import HealthScoreGauge from "../components/HealthScoreGauge";
import { StreakBadge } from "../components/StreakAndNBA";
import { BentoTile, MetricTile, InsightTile } from "../components/Bento";
import SignalVsSelf from "../components/SignalVsSelf";
import { useNavigate } from "react-router-dom";

// Iter 51 — `TIP`, `KpiCard`, `MetricCard`, `ChartTooltip` extracted to
// `/app/frontend/src/lib/dashboardTips.js` and
// `/app/frontend/src/components/dashboard/Cards.jsx`.

export default function Dashboard() {
  const { currency } = useCurrency();
  const { theme } = useTheme();
  const { language } = useLanguage();
  // ── Chart palette flips with theme so light mode reads cleanly. ──
  const chartC = theme === "light"
    ? { line: "#059669", grid: "#f1f5f9", text: "#94A3B8", axis: "#e2e8f0",
        gradTop: "rgba(5,150,105,0.20)", gradBot: "rgba(5,150,105,0)",
        dotFill: "#ffffff", cursor: "rgba(5,150,105,0.3)" }
    : { line: "#00E5A0", grid: "#27272A", text: "#A1A1AA", axis: "#27272A",
        gradTop: "rgba(0,229,160,0.45)", gradBot: "rgba(0,229,160,0)",
        dotFill: "#09090B", cursor: "rgba(0,229,160,0.3)" };
  const nav = useNavigate();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  // Iter 51 — Behavioral Insights Drawer state. Click on any of the 4
  // InsightTiles opens the side drawer with rich detail + suggested fixes.
  const [drawerKey, setDrawerKey] = useState(null);
  const openDrawer = (k) => () => setDrawerKey(k);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        // Iter 21 — cache-bust query param so Cloudflare/browser never
        // serves a stale dashboard after a fresh trade save.
        // Iter 53 — pass current language so backend produces Hinglish/EN
        // copy for Health Score message + Next Best Action.
        const { data } = await api.get(`/dashboard/stats?lang=${language}&_t=${Date.now()}`);
        if (!cancelled) setStats(data);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [language]);

  const chartData = useMemo(() => {
    if (!stats) return [];
    return stats.series.map((s) => ({
      ...s,
      value: currency === "INR" ? s.cumulative_inr : s.cumulative_usd,
    }));
  }, [stats, currency]);

  const dailyMap = useMemo(() => {
    if (!stats) return {};
    const m = {};
    (stats.daily_pnl || stats.series || []).forEach((d) => {
      m[d.date] = currency === "INR" ? d.daily_inr : d.daily_usd;
    });
    return m;
  }, [stats, currency]);

  const breakdownItems = useMemo(() => {
    if (!stats) return { symbols: [], brokers: [] };
    const map = (arr, keyField) =>
      (arr || []).map((b) => ({
        label: b[keyField],
        count: b.count,
        win_rate: b.win_rate,
        total_pnl: currency === "INR" ? b.total_pnl_inr : b.total_pnl_usd,
      }));
    return {
      symbols: map(stats.by_symbol, "symbol"),
      brokers: map(stats.by_broker, "broker"),
    };
  }, [stats, currency]);

  const totalPnl = stats ? (currency === "INR" ? stats.total_pnl_inr : stats.total_pnl_usd) : 0;
  const pnlPositive = totalPnl >= 0;

  // ── Iter 13: derived premium analytics ──
  const profitFactor = stats ? (currency === "INR" ? stats.profit_factor_inr : stats.profit_factor_usd) : null;
  const avgWin = stats ? (currency === "INR" ? stats.avg_win_inr : stats.avg_win_usd) : 0;
  const avgLoss = stats ? (currency === "INR" ? stats.avg_loss_inr : stats.avg_loss_usd) : 0;
  const maxDD = stats ? (currency === "INR" ? stats.max_drawdown_inr : stats.max_drawdown_usd) : null;
  const maxDDPct = stats?.max_drawdown_pct ?? null;
  const bestStreak = stats?.best_streak ?? 0;
  const worstStreak = stats?.worst_streak ?? 0;
  const isPerfect = stats && (currency === "INR" ? stats.profit_factor_inr : stats.profit_factor_usd) == null && (stats?.losses ?? 0) === 0 && (stats?.wins ?? 0) > 0;
  const pfColor =
    isPerfect ? "#00ff88"
    : profitFactor == null ? "#6b7280"
    : profitFactor >= 1.5 ? "#00ff88"
    : profitFactor >= 1.0 ? "#fbbf24"
    : "#ef4444";

  // ── Iter 17: Prop Firm earnings (card hidden when user has zero prop trades) ──
  const propTradeCount = stats?.prop_trade_count ?? 0;
  const propEarnings = stats
    ? (currency === "INR" ? stats.prop_earnings_inr : stats.prop_earnings_usd)
    : null;
  const showPropEarnings = propTradeCount > 0 && propEarnings != null;

  // ── Iter 18: Exit Discipline analytics ──
  // Hidden until the user has ≥1 non-MANUAL exit tag in the journal (i.e. they've
  // actually started tagging their exits). All metrics use the user's full
  // trade history regardless of currency.
  const ec = stats?.exit_counts || { SL_HIT: 0, TP_HIT: 0, CUT_EARLY: 0, MANUAL: 0 };
  const taggedTotal = ec.SL_HIT + ec.TP_HIT + ec.CUT_EARLY; // exclude MANUAL from the denominator
  const showExitDiscipline = taggedTotal > 0;
  const slDisciplinePct = taggedTotal > 0 ? Math.round((ec.SL_HIT / taggedTotal) * 100) : 0;
  const tpHitPct        = taggedTotal > 0 ? Math.round((ec.TP_HIT / taggedTotal) * 100) : 0;
  const cutEarlyPct     = taggedTotal > 0 ? Math.round((ec.CUT_EARLY / taggedTotal) * 100) : 0;
  const exitInsight = (() => {
    if (!showExitDiscipline) return null;
    if (cutEarlyPct > 30) return { icon: "⚠️", text: "You cut 30%+ of trades early — you may be exiting winners too soon", tone: "warn" };
    if (tpHitPct > 60) return { icon: "✅", text: "Strong TP discipline — you're letting winners run", tone: "good" };
    return { icon: "📊", text: "Keep logging trades to unlock your exit pattern insights", tone: "muted" };
  })();

  // ── Iter 27: Stop Loss Discipline (behavioral insight) ──
  // Pure logic over the user's own trades. Card only renders once the user has
  // at least 5 logged trades — below that, the percentages are too noisy.
  const sl = stats?.sl_discipline || {
    trades_with_sl: 0, trades_without_sl: 0,
    losses_with_sl: 0, losses_without_sl: 0,
    discipline_pct: 0, losses_without_sl_pct: 0, total_losses: 0,
    source_gap: false, sources_missing_sl_data: [],
  };
  const totalTradesCount = stats?.total_trades ?? 0;
  // ── Iter 36: Low trade-count guardrail ──
  // Cards that become statistically extreme at low sample sizes (Profit
  // Factor, per-source/broker/symbol breakdowns, Pattern of the Week)
  // render a "Building your profile" placeholder until this threshold.
  const lowSample = totalTradesCount < MIN_TRADES_FOR_INSIGHT;
  const tradesRemaining = Math.max(0, MIN_TRADES_FOR_INSIGHT - totalTradesCount);
  const showSlDiscipline = totalTradesCount >= 5;
  // Iter 127 — SL data structurally absent for this account's data
  // sources (e.g. Delta CSV stores stop_loss as a separate order row,
  // not on the parent trade). When true we don't judge discipline at
  // all — we show a neutral "unavailable" verdict + how to unlock.
  const slSourceGap = !!sl.source_gap;
  const slBelowThreshold = !slSourceGap && sl.discipline_pct < 60;

  // ── Iter 29: Overtrading detection (behavioral insight) ──
  // Card only renders once the user has ≥5 trades across ≥2 different days.
  const ot = stats?.overtrading || {
    active_days: 0, total_trades: 0, avg_per_day: 0,
    high_vol_days: 0, high_vol_trades: 0, max_trades_in_a_day: 0,
    win_rate_high_vol: 0, win_rate_normal: 0,
  };
  const showOvertrading = totalTradesCount >= 5 && ot.active_days >= 2;
  const overtradingFlagged = ot.high_vol_days > 0;

  // ── Iter 30: Revenge Trading (behavioral insight) ──
  // Pattern: after-loss trades have a notably worse win rate than normal trades.
  const rv = stats?.revenge || {
    revenge_count: 0, revenge_wins: 0, revenge_emotional: 0, revenge_pnl_usd: 0,
    normal_count: 0, normal_wins: 0, normal_pnl_usd: 0,
    revenge_win_rate: 0, normal_win_rate: 0,
    emotion_gap: false, user_tagged_emotion_count: 0,
  };
  // Gate: need ≥5 trades AND at least 1 revenge candidate (i.e. ≥1 loss followed
  // by another trade). When fewer, the card prompts the user to log more.
  const showRevenge = totalTradesCount >= 5 && rv.revenge_count >= 1;
  // Iter 127 — if no user has ever explicitly tagged a trade with an
  // active emotion, the "Emotional tags" side of this card must not
  // read as a positive signal. Frontend swaps that side to a "log
  // emotions to unlock" nudge and only lets the win-rate delta drive
  // the verdict.
  const emotionGap = !!rv.emotion_gap;
  // Flag when after-loss win rate is meaningfully worse (≥10pp gap) than normal,
  // OR when revenge_emotional tags are visible. When emotion data is
  // unavailable (emotionGap) we rely solely on the win-rate delta so we
  // never fabricate an "Emotional Control" verdict from zero data.
  const revengeFlagged = showRevenge && (
    (rv.normal_win_rate - rv.revenge_win_rate) >= 10 ||
    (!emotionGap && rv.revenge_emotional > 0)
  );

  // ── Iter 31: "Your Edge" — best & worst pattern across source/symbol/emotion/side ──
  const edge = stats?.edge || { eligible: false, best: null, worst: null, candidates_total: 0 };
  const showEdge = totalTradesCount >= 5 && edge.eligible && edge.best && edge.worst;
  const DIM_LABELS = { source: "Trade source", symbol: "Symbol", emotion: "Emotion tag", side: "Direction" };
  const formatUsd = (n) => {
    const s = Math.abs(n) >= 1000 ? `$${Math.round(n).toLocaleString()}` : `$${n.toFixed(2)}`;
    return n < 0 ? `-${s.replace("-", "")}` : s;
  };

  // ── Empty state — show only when there are zero trades ──
  const showEmptyState = !loading && stats && (stats.total_trades ?? 0) === 0;

  if (showEmptyState) {
    return <DashboardEmptyState />;
  }

  return (
    <div className="space-y-8" data-testid="dashboard-page">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
        <div>
          <p className="bt-label">Overview</p>
          <h1 className="font-heading text-4xl sm:text-5xl tracking-tight">Dashboard</h1>
          <div className="flex items-center gap-3 flex-wrap mt-1">
            <p className="text-sm text-[var(--bt-text-muted)]">
              Your trading edge — at a glance.
            </p>
            {/* iter122 — data confidence pill: quick signal that the
                KPIs below can be trusted (or need a nudge). */}
            <DataConfidencePill testid="dashboard-confidence-pill" />
          </div>
        </div>
        <Link
          to="/trades/new"
          data-testid="dashboard-add-trade-btn"
          className="inline-flex items-center gap-2 px-4 py-2.5 bg-[var(--bt-accent)] hover:bg-[var(--bt-accent-hover)] text-black font-bold rounded-sm transition-colors duration-150"
        >
          + Log Trade <ArrowUpRight className="w-4 h-4" />
        </Link>
      </div>

      {/* iter134 D5a — mini Day Zero setup banner. Self-hides when
          status is completed or abandoned (skip-for-now respected). */}
      <DayZeroChat variant="mini" />

      {/* iter134 D4 — mini thesis-gate banner. Renders only when the
          user has pending live-entry prompts; otherwise self-hides. */}
      <ThesisGateBanner variant="mini" />

      {/* ═══════════════ Iter 35 — Bento grid hero section ═══════════════
          4-col on desktop · 2-col tablet · 1-col mobile.
          Health Score (2×2 hero) · NBA (2×1 glass) · 4 metrics · 4 insights · chart full-width. */}
      <div className="bt-bento-grid">
        {/* HEALTH SCORE — hero (2 cols × 2 rows) */}
        {stats?.health_score && (
          <BentoTile span="bt-tile-hero" className="p-0 overflow-hidden">
            <HealthScoreGauge
              score={stats.health_score.score}
              color={stats.health_score.color}
              locked={stats.health_score.locked}
              label={stats.health_score.label}
              message={stats.health_score.message}
              reliability={stats.health_score.reliability}
              tradesLogged={stats.health_score.trades_logged}
              tradesNeeded={stats.health_score.trades_needed}
            />
          </BentoTile>
        )}

        {/* NEXT BEST ACTION — glass tile (2 cols × 1 row) */}
        {stats?.next_best_action && (
          <BentoTile span="bt-tile-wide" className="bt-glass-card">
            <div className="flex items-start gap-3">
              <span className="bt-live-dot inline-block rounded-full mt-1.5" style={{ width: 8, height: 8 }} aria-hidden="true" />
              <div className="flex-1 min-w-0">
                <p className="bt-label flex items-center gap-2">
                  Next Best Action
                  <span className="text-[10px] uppercase tracking-wider text-[var(--bt-text-muted)]">· live</span>
                </p>
                <p className="font-heading text-xl sm:text-2xl mt-1.5">
                  <span aria-hidden="true" className="mr-1">{stats.next_best_action.icon}</span>
                  {stats.next_best_action.title}
                </p>
                <p className="text-sm text-[var(--bt-text-muted)] mt-1.5 leading-relaxed" data-testid="nba-message">
                  {stats.next_best_action.message}
                </p>
              </div>
            </div>
          </BentoTile>
        )}

        {/* STREAK — 2 cols × 1 row (compact horizontal layout) */}
        {stats?.streak && (
          <BentoTile span="bt-tile-wide">
            <StreakBadge streak={stats.streak} />
          </BentoTile>
        )}

        {/* 4 METRIC TILES (1×1 each) */}
        <MetricTile
          featured
          label={<>Expectancy <MetricInfoTooltip title={TIP.expectancy.title} description={TIP.expectancy.description} testId="tip-expectancy" /></>}
          testId="metric-expectancy"
          value={stats ? `$${((stats.win_rate / 100) * (stats.avg_win || 0) - (1 - stats.win_rate / 100) * Math.abs(stats.avg_loss || 0)).toFixed(2)}` : "—"}
          valueColor="var(--bt-accent)"
          context="Avg P&L per trade — your true edge in dollars."
        />
        <MetricTile
          label="Net P&L"
          testId="metric-net-pnl"
          value={loading ? "—" : formatMoney(totalPnl, currency)}
          valueColor={pnlPositive ? "var(--bt-win)" : "var(--bt-loss)"}
          context={`${stats?.total_trades ?? 0} trades · ${stats?.wins ?? 0}W / ${stats?.losses ?? 0}L`}
        />
        <MetricTile
          label={<>Profit Factor <MetricInfoTooltip title={TIP.profit_factor.title} description={TIP.profit_factor.description} testId="tip-profit-factor" /></>}
          testId="metric-profit-factor"
          value={
            lowSample
              ? <BuildingProfileNotice tradesRemaining={tradesRemaining} testId="metric-profit-factor-placeholder" />
              : (stats?.profit_factor != null ? stats.profit_factor.toFixed(2) : "—")
          }
          valueColor={lowSample ? "var(--bt-text-muted)" : ((stats?.profit_factor || 0) >= 1.5 ? "var(--bt-win)" : "var(--bt-warn)")}
          context={lowSample ? null : "Gross wins ÷ gross losses. Above 1.5 is healthy."}
        />
        <MetricTile
          label="Win Rate · R:R"
          testId="metric-wr-rr"
          value={stats ? `${(stats.win_rate || 0).toFixed(2)}% · ${(stats.avg_r_multiple || 0).toFixed(2)}R` : "—"}
          valueColor="var(--bt-text)"
          context="Win rate combined with average risk:reward."
        />

        {/* 4 BEHAVIORAL INSIGHT TILES (1×1 each, clickable → drawer) */}
        <InsightTile
          icon="🛡️"
          title="Stop Loss Discipline"
          testId="insight-sl"
          onClick={openDrawer("sl")}
          tone={slSourceGap ? "muted" : ((stats?.sl_discipline?.discipline_pct ?? 100) >= 60 ? "good" : "warn")}
          summary={
            slSourceGap ? (
              <span className="text-[var(--bt-text-muted)]">Not in CSV — <span className="underline">unlock</span></span>
            ) : (
              <>
                <span className="bt-num font-semibold">{(stats?.sl_discipline?.discipline_pct ?? 0).toFixed(0)}%</span>
                {" "}with SL · <span className="bt-num">{stats?.sl_discipline?.trades_without_sl ?? 0}</span> without
              </>
            )
          }
          detail={
            slSourceGap
              ? "Stop-loss data isn't available from this import format. Connect via API or log SL manually to unlock."
              : `${stats?.sl_discipline?.losses_without_sl_pct ?? 0}% of your losses had no stop loss. ${stats?.sl_discipline?.trades_with_sl ?? 0} of ${stats?.total_trades ?? 0} trades used SL.`
          }
        />
        <InsightTile
          icon="⏱️"
          title="Trade Frequency"
          testId="insight-frequency"
          onClick={openDrawer("frequency")}
          tone={(stats?.overtrading?.high_vol_days ?? 0) > 0 ? "warn" : "good"}
          summary={
            <>
              <span className="bt-num font-semibold">{(stats?.overtrading?.avg_per_day ?? 0).toFixed(1)}</span>
              /day · <span className="bt-num">{stats?.overtrading?.high_vol_days ?? 0}</span> high-vol days
            </>
          }
          detail={`Peak day: ${stats?.overtrading?.max_trades_in_a_day ?? 0} trades. Win rate on high-volume days: ${(stats?.overtrading?.win_rate_high_vol ?? 0).toFixed(2)}% vs ${(stats?.overtrading?.win_rate_normal ?? 0).toFixed(2)}% normal.`}
        />
        <InsightTile
          icon="🧘"
          title="Emotional Control"
          testId="insight-emotional"
          onClick={openDrawer("emotional")}
          tone={
            (stats?.revenge?.revenge_count ?? 0) > 0 &&
            ((stats?.revenge?.normal_win_rate ?? 0) - (stats?.revenge?.revenge_win_rate ?? 0) >= 10)
              ? "warn" : "good"
          }
          summary={
            <>
              After-loss WR: <span className="bt-num font-semibold">{(stats?.revenge?.revenge_win_rate ?? 0).toFixed(2)}%</span>
              {" "}vs normal <span className="bt-num">{(stats?.revenge?.normal_win_rate ?? 0).toFixed(2)}%</span>
            </>
          }
          detail={
            stats?.revenge?.emotion_gap
              ? `${stats?.revenge?.revenge_count ?? 0} trades taken right after a loss. Emotion tags aren't logged on your trades — log Revenge / FOMO / Greedy to unlock the full read.`
              : `${stats?.revenge?.revenge_count ?? 0} trades taken right after a loss. ${stats?.revenge?.revenge_emotional ?? 0} tagged Revenge / FOMO / Greedy.`
          }
        />
        <InsightTile
          icon="🏆"
          title="Your Edge"
          testId="insight-edge"
          onClick={openDrawer("edge")}
          tone={stats?.edge?.eligible ? "good" : "warn"}
          summary={
            stats?.edge?.eligible ? (
              <>
                Best: <span className="font-semibold">{stats.edge.best.label}</span>
                {" "}(<span className="bt-num">{stats.edge.best.win_rate.toFixed(2)}%</span>)
              </>
            ) : "Log more varied trades to unlock"
          }
          detail={
            stats?.edge?.eligible
              ? `Strength: ${stats.edge.best.label} — ${stats.edge.best.count} trades, ${stats.edge.best.win_rate.toFixed(2)}% WR. Watch out: ${stats.edge.worst.label} — ${stats.edge.worst.count} trades, ${stats.edge.worst.win_rate.toFixed(2)}% WR.`
              : "Need at least 2 buckets with 2+ trades each across source / symbol / emotion / direction."
          }
        />
      </div>

      {/* Pattern of the Week */}
      {stats && (
        <PatternOfTheWeek
          stats={stats}
          currency={currency}
          lowSample={lowSample}
          tradesRemaining={tradesRemaining}
          language={language}
        />
      )}

      {/* KPIs */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <KpiCard
          testid="kpi-total-pnl"
          label="Total Net P&L"
          value={loading ? "—" : formatMoney(totalPnl, currency)}
          accent={pnlPositive ? "text-[var(--bt-accent)]" : "text-[var(--bt-danger)]"}
          icon={pnlPositive ? TrendingUp : TrendingDown}
          sublabel={stats ? `${stats.wins} wins · ${stats.losses} losses · after fees` : ""}
        />
        <KpiCard
          testid="kpi-win-rate"
          label="Win Rate"
          value={loading ? "—" : `${(stats?.win_rate ?? 0).toFixed(2)}%`}
          accent={(stats?.win_rate ?? 0) >= 50 ? "text-[var(--bt-accent)]" : "text-white"}
          icon={Target}
          sublabel="profitable trades"
        />
        <KpiCard
          testid="kpi-avg-r"
          label={<>Avg Risk:Reward <MetricInfoTooltip title={TIP.avg_rr.title} description={TIP.avg_rr.description} testId="tip-avg-rr" /></>}
          value={loading ? "—" : stats?.avg_r_multiple == null ? "—" : `1 : ${stats.avg_r_multiple.toFixed(1)}`}
          accent={(stats?.avg_r_multiple ?? 0) >= 0 ? "text-[var(--bt-accent)]" : "text-[var(--bt-danger)]"}
          icon={Scale}
          sublabel={stats ? `${stats.r_count ?? 0} trades with stop loss` : ""}
        />
        <KpiCard
          testid="kpi-total-trades"
          label="Total Trades"
          value={loading ? "—" : (stats?.total_trades ?? 0)}
          icon={Activity}
          sublabel="logged in journal"
        />
      </div>

      {/* iter121 — Forced-exit notice. Renders ONLY when the backend
          excluded liquidations/ADL from the visible stats. Explains the
          gap between what the user saw on Delta (all fills) vs what the
          dashboard shows (discretionary trades only). Silent when no
          forced exits — zero visual noise for CSV/manual accounts. */}
      {!loading && (stats?.forced_exits_count ?? 0) > 0 && (
        <div
          data-testid="forced-exits-notice"
          className="flex items-start gap-2 rounded-md px-3 py-2 text-xs sm:text-sm border"
          style={{
            background: "color-mix(in srgb, #ff9500 8%, var(--bt-surface))",
            borderColor: "color-mix(in srgb, #ff9500 35%, var(--bt-border))",
            color: "var(--bt-text)",
          }}
        >
          <span aria-hidden="true" className="mt-0.5" style={{ color: "#ff9500" }}>⚠</span>
          <div className="flex-1 min-w-0">
            <span className="font-semibold">
              Showing <span data-testid="forced-exits-visible">{stats.total_trades}</span> trade{stats.total_trades === 1 ? "" : "s"}
            </span>
            {" "}
            <span className="text-[var(--bt-text-muted)]">
              (<span data-testid="forced-exits-excluded-count">{stats.forced_exits_count}</span>
              {" "}excluded as forced exit{stats.forced_exits_count === 1 ? "" : "s"})
            </span>
            <div className="text-[11px] sm:text-xs text-[var(--bt-text-muted)] mt-0.5 leading-relaxed">
              Liquidations and ADL closures aren't discretionary decisions, so we leave them out of behavioural analytics. They're still visible on the Trades page with a chip.
            </div>
          </div>
        </div>
      )}

      {/* ─── Iter 17: Prop Firm Earnings — hidden entirely until first prop trade ─── */}
      {showPropEarnings && (
        <MetricCard
          testid="metric-prop-earnings"
          label="🏆 PROP EARNINGS"
          icon={Trophy}
          tooltip="Total profit you kept after prop firm profit split (Net P&L × your share)."
        >
          <div className="flex items-baseline gap-3 flex-wrap">
            <span
              data-testid="metric-prop-earnings-value"
              style={{
                color: propEarnings >= 0 ? "#00ff88" : "#ef4444",
                fontWeight: 700,
              }}
            >
              {formatMoney(propEarnings, currency)}
            </span>
            <span className="text-[11px] text-[#6b7280] font-medium">
              · {propTradeCount} prop trade{propTradeCount === 1 ? "" : "s"} · Your total after firm split
            </span>
          </div>
        </MetricCard>
      )}

      {/* ─── Iter 18: Exit Discipline — hidden until user has ≥1 tagged exit ─── */}
      {showExitDiscipline && (
        <section
          data-testid="exit-discipline-section"
          className="bt-card bg-[var(--bt-surface)] border border-[var(--bt-border)] rounded-sm p-6"
          data-card="true"
        >
          <div className="flex items-center justify-between flex-wrap gap-2 mb-5">
            <h3 className="font-heading text-sm uppercase tracking-[0.18em] text-[var(--bt-text-muted)] flex items-center gap-2">
              Exit Discipline Analysis
              <MetricInfoTooltip
                title={TIP.exit_discipline.title}
                description={TIP.exit_discipline.description}
                testId="tip-exit-discipline"
              />
            </h3>
            <span className="text-[11px] text-[var(--bt-text-muted)] font-mono">
              {taggedTotal} tagged exit{taggedTotal === 1 ? "" : "s"}
            </span>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            <div data-testid="exit-discipline-tp">
              <p className="bt-label">TP Hit Rate</p>
              <p className="font-heading text-4xl font-bold bt-num mt-2" style={{ color: "#00ff88" }}>
                {tpHitPct}%
              </p>
              <p className="text-xs text-[var(--bt-text-muted)] mt-1">
                Full target · {ec.TP_HIT} hit
              </p>
            </div>
            <div data-testid="exit-discipline-cut">
              <p className="bt-label">Cut Early</p>
              <p className="font-heading text-4xl font-bold bt-num mt-2" style={{ color: "#f59e0b" }}>
                {cutEarlyPct}%
              </p>
              <p className="text-xs text-[var(--bt-text-muted)] mt-1">
                Early out · {ec.CUT_EARLY} cut
              </p>
            </div>
          </div>
          {exitInsight && (
            <p
              data-testid="exit-discipline-insight"
              className="mt-5 pt-4 border-t border-dashed border-[var(--bt-border)] text-sm"
              style={{
                color: exitInsight.tone === "good"
                  ? "var(--bt-accent)"
                  : exitInsight.tone === "warn"
                    ? "#f59e0b"
                    : "var(--bt-text-muted)",
              }}
            >
              <span className="mr-1.5">{exitInsight.icon}</span>
              {exitInsight.text}
            </p>
          )}
        </section>
      )}

      {/* ─── Iter 27: Stop Loss Discipline insight ─── */}
      {totalTradesCount > 0 && (
        <section
          data-testid="sl-discipline-section"
          className="bt-card p-6 space-y-3"
        >
          <div className="flex items-center justify-between flex-wrap gap-2">
            <p className="bt-label flex items-center gap-2">
              <span aria-hidden="true">🛡️</span> Stop Loss Discipline
              <MetricInfoTooltip
                title={TIP.sl_discipline.title}
                description={TIP.sl_discipline.description}
                testId="tip-sl-discipline"
              />
            </p>
            <p className="text-xs text-[var(--bt-text-muted)]">
              {totalTradesCount}{" "}
              trade{totalTradesCount === 1 ? "" : "s"} analyzed
            </p>
          </div>

          {!showSlDiscipline ? (
            <p
              data-testid="sl-discipline-locked"
              className="text-sm text-[var(--bt-text-muted)] py-2"
            >
              Log a few more trades to unlock your Stop Loss insight (need at least 5).
            </p>
          ) : slSourceGap ? (
            /* Iter 127 — Structural gap: this account's data source
               (e.g. Delta CSV or Delta API) doesn't carry a stop_loss
               field on the trade. Iter 129 — source-aware copy so a
               delta_api user isn't told to "connect via API" (they
               already are). */
            (() => {
              const srcs = sl.sources_missing_sl_data || [];
              const hasApi = srcs.includes("delta_api");
              const hasCsv = srcs.includes("csv");
              const label = srcs.map((s) => s === "csv" ? "CSV" : s === "delta_api" ? "Delta API" : s).join(", ");
              // "Why" copy: same reality (Delta's SL orders live on
              // separate rows / not on /v2/fills). Phrasing differs
              // slightly between CSV-only and API-only users.
              const whyCopy = hasApi && !hasCsv
                ? "Delta's fills API doesn't expose the stop-loss trigger on closed positions — it lives on a separate order object we don't currently fetch."
                : "Exchanges like Delta export stop-loss as a separate order row, not as a field on the trade — so we can't verdict discipline honestly from this data alone.";
              // "How to unlock" copy tailored per source.
              const unlockCopy = hasApi && !hasCsv
                ? "Log a stop_loss on trades manually from the Trades page. (Full API-side SL recovery is on the roadmap.)"
                : hasCsv && !hasApi
                  ? "Re-import your CSV — the parser now recovers stop-loss from STOP_LOSS_ORDER rows. Or log a stop_loss on trades manually from the Trades page."
                  : "Re-import your CSV to recover CSV-side stop-loss, or log a stop_loss on trades manually from the Trades page.";
              return (
                <div
                  data-testid="sl-discipline-source-gap"
                  className="rounded-md p-4 border"
                  style={{
                    background: "color-mix(in srgb, var(--bt-text-muted) 8%, transparent)",
                    borderColor: "var(--bt-border)",
                  }}
                >
                  <p className="text-sm leading-relaxed text-[var(--bt-text)]">
                    <span className="font-semibold">Stop-loss data isn&apos;t available from your import format.</span>
                    {" "}
                    {label && (
                      <span className="text-[var(--bt-text-muted)]">({label}){" "}</span>
                    )}
                    {whyCopy}
                  </p>
                  <p className="text-xs text-[var(--bt-text-muted)] mt-2">
                    <span className="font-semibold">To unlock this insight:</span>{" "}
                    {unlockCopy}
                  </p>
                </div>
              );
            })()
          ) : (
            <>
              {/* Headline % + breakdown stats — JetBrains Mono per design system */}
              <div className="grid grid-cols-3 gap-3 sm:gap-4">
                <div>
                  <p className="bt-label text-[10px]">Discipline</p>
                  <p
                    data-testid="sl-discipline-pct"
                    className="bt-num text-3xl mt-1"
                    style={{ color: slBelowThreshold ? "var(--bt-loss)" : "var(--bt-win)" }}
                  >
                    {sl.discipline_pct.toFixed(0)}%
                  </p>
                  <p className="text-[11px] text-[var(--bt-text-muted)] mt-0.5">
                    <span data-testid="sl-trades-with" className="bt-num">{sl.trades_with_sl}</span>{" "}
                    with · <span data-testid="sl-trades-without" className="bt-num">{sl.trades_without_sl}</span>{" "}
                    without
                  </p>
                </div>
                <div>
                  <p className="bt-label text-[10px]">No-SL Losses</p>
                  <p
                    data-testid="sl-no-loss-pct"
                    className="bt-num text-3xl mt-1"
                    style={{
                      color: sl.losses_without_sl_pct >= 50 ? "var(--bt-loss)" : "var(--bt-text)",
                    }}
                  >
                    {sl.losses_without_sl_pct.toFixed(0)}%
                  </p>
                  <p className="text-[11px] text-[var(--bt-text-muted)] mt-0.5">
                    of <span className="bt-num">{sl.total_losses}</span> losses had no stop
                  </p>
                </div>
                <div>
                  <p className="bt-label text-[10px]">Trades w/ SL</p>
                  <p
                    data-testid="sl-trades-with-big"
                    className="bt-num text-3xl mt-1"
                    style={{ color: "var(--bt-accent)" }}
                  >
                    {sl.trades_with_sl}
                  </p>
                  <p className="text-[11px] text-[var(--bt-text-muted)] mt-0.5">
                    out of <span className="bt-num">{totalTradesCount}</span>
                  </p>
                </div>
              </div>

              {/* Verdict callout — warning below 60%, positive at 60%+ */}
              <div
                data-testid="sl-discipline-insight"
                className="rounded-md p-4 mt-2 border"
                style={{
                  background: slBelowThreshold
                    ? "color-mix(in srgb, var(--bt-loss) 10%, transparent)"
                    : "color-mix(in srgb, var(--bt-win) 12%, transparent)",
                  borderColor: slBelowThreshold
                    ? "color-mix(in srgb, var(--bt-loss) 50%, transparent)"
                    : "color-mix(in srgb, var(--bt-win) 50%, transparent)",
                }}
              >
                <p
                  className="text-sm leading-relaxed"
                  style={{ color: slBelowThreshold ? "var(--bt-loss)" : "var(--bt-win)" }}
                >
                  {slBelowThreshold ? (
                    <>
                      <span className="font-semibold">⚠️ Stop Loss Discipline:{" "}
                        <span className="bt-num">{sl.discipline_pct.toFixed(0)}%</span></span>
                      {" "}— You're trading without a stop loss on{" "}
                      <span className="bt-num font-semibold">{sl.trades_without_sl}</span>{" "}
                      trade{sl.trades_without_sl === 1 ? "" : "s"}.{" "}
                      {sl.total_losses > 0 && (
                        <>
                          <span className="bt-num font-semibold">{sl.losses_without_sl_pct.toFixed(0)}%</span>{" "}
                          of your losses had no stop loss.{" "}
                        </>
                      )}
                      Setting a stop loss on every trade is the #1 habit that separates consistent traders.
                    </>
                  ) : (
                    <>
                      <span className="font-semibold">✅ Stop Loss Discipline:{" "}
                        <span className="bt-num">{sl.discipline_pct.toFixed(0)}%</span></span>
                      {" "}— Strong risk control. You set a stop loss on most trades
                      (<span className="bt-num">{sl.trades_with_sl}</span> of{" "}
                      <span className="bt-num">{totalTradesCount}</span>).
                    </>
                  )}
                </p>
              </div>
            </>
          )}
        </section>
      )}

      {/* ─── Iter 29: Overtrading insight — same style as Stop Loss Discipline ─── */}
      {totalTradesCount > 0 && (
        <section
          data-testid="overtrading-section"
          className="bt-card p-6 space-y-3"
        >
          <div className="flex items-center justify-between flex-wrap gap-2">
            <p className="bt-label flex items-center gap-2">
              <span aria-hidden="true">⏱️</span> Trade Frequency
            </p>
            <p className="text-xs text-[var(--bt-text-muted)]">
              <span className="bt-num">{ot.active_days}</span>{" "}
              active day{ot.active_days === 1 ? "" : "s"} ·{" "}
              <span className="bt-num">{totalTradesCount}</span> trade{totalTradesCount === 1 ? "" : "s"}
            </p>
          </div>

          {!showOvertrading ? (
            <p
              data-testid="overtrading-locked"
              className="text-sm text-[var(--bt-text-muted)] py-2"
            >
              Log more trades to unlock your frequency insight (need at least 5 trades across 2+ days).
            </p>
          ) : (
            <>
              <div className="grid grid-cols-3 gap-3 sm:gap-4">
                <div>
                  <p className="bt-label text-[10px]">Avg / Day</p>
                  <p
                    data-testid="overtrading-avg"
                    className="bt-num text-3xl mt-1"
                    style={{ color: "var(--bt-text)" }}
                  >
                    {ot.avg_per_day.toFixed(1)}
                  </p>
                  <p className="text-[11px] text-[var(--bt-text-muted)] mt-0.5">
                    across <span className="bt-num">{ot.active_days}</span> day{ot.active_days === 1 ? "" : "s"}
                  </p>
                </div>
                <div>
                  <p className="bt-label text-[10px]">Peak Day</p>
                  <p
                    data-testid="overtrading-peak"
                    className="bt-num text-3xl mt-1"
                    style={{
                      color: ot.max_trades_in_a_day >= 5
                        ? "var(--bt-loss)"
                        : "var(--bt-text)",
                    }}
                  >
                    {ot.max_trades_in_a_day}
                  </p>
                  <p className="text-[11px] text-[var(--bt-text-muted)] mt-0.5">
                    trades in one day
                  </p>
                </div>
                <div>
                  <p className="bt-label text-[10px]">High-Vol Days</p>
                  <p
                    data-testid="overtrading-high-vol"
                    className="bt-num text-3xl mt-1"
                    style={{
                      color: overtradingFlagged ? "var(--bt-loss)" : "var(--bt-win)",
                    }}
                  >
                    {ot.high_vol_days}
                  </p>
                  <p className="text-[11px] text-[var(--bt-text-muted)] mt-0.5">
                    over <span className="bt-num">2×</span> avg or <span className="bt-num">≥5</span>
                  </p>
                </div>
              </div>

              <div
                data-testid="overtrading-insight"
                className="rounded-md p-4 mt-2 border"
                style={{
                  background: overtradingFlagged
                    ? "color-mix(in srgb, var(--bt-loss) 10%, transparent)"
                    : "color-mix(in srgb, var(--bt-win) 12%, transparent)",
                  borderColor: overtradingFlagged
                    ? "color-mix(in srgb, var(--bt-loss) 50%, transparent)"
                    : "color-mix(in srgb, var(--bt-win) 50%, transparent)",
                }}
              >
                <p
                  className="text-sm leading-relaxed"
                  style={{ color: overtradingFlagged ? "var(--bt-loss)" : "var(--bt-win)" }}
                >
                  {overtradingFlagged ? (
                    <>
                      <span className="font-semibold">⚠️ Overtrading detected</span>
                      {" "}— On <span className="bt-num font-semibold">{ot.high_vol_days}</span>{" "}
                      day{ot.high_vol_days === 1 ? "" : "s"} you took{" "}
                      <span className="bt-num font-semibold">{ot.high_vol_trades}</span>{" "}
                      trade{ot.high_vol_trades === 1 ? "" : "s"} (your average is{" "}
                      <span className="bt-num font-semibold">{ot.avg_per_day.toFixed(1)}</span>/day).
                      {" "}Win rate on high-volume days:{" "}
                      <span className="bt-num font-semibold">{ot.win_rate_high_vol.toFixed(2)}%</span>
                      {" "}vs <span className="bt-num font-semibold">{ot.win_rate_normal.toFixed(2)}%</span>{" "}
                      on normal days. More trades doesn't mean more profit.
                    </>
                  ) : (
                    <>
                      <span className="font-semibold">✅ Trade Frequency: Healthy</span>
                      {" "}— You're not overtrading. Your activity is controlled
                      ({" "}
                      <span className="bt-num">{ot.avg_per_day.toFixed(1)}</span>{" "}
                      trade{ot.avg_per_day === 1 ? "" : "s"}/day average).
                    </>
                  )}
                </p>
              </div>
            </>
          )}
        </section>
      )}

      {/* ─── Iter 30: Revenge Trading insight — same style as the other behavioral cards ─── */}
      {totalTradesCount > 0 && (
        <section
          data-testid="revenge-section"
          className="bt-card p-6 space-y-3"
        >
          <div className="flex items-center justify-between flex-wrap gap-2">
            <p className="bt-label flex items-center gap-2">
              <span aria-hidden="true">🎯</span>{" "}
              {revengeFlagged ? "Revenge Trading" : (emotionGap ? "After-Loss Behavior" : "Emotional Control")}
              <MetricInfoTooltip
                title={TIP.emotional_control.title}
                description={TIP.emotional_control.description}
                testId="tip-emotional-control"
              />
            </p>
            <p className="text-xs text-[var(--bt-text-muted)]">
              <span className="bt-num">{rv.revenge_count}</span>{" "}
              after-loss · <span className="bt-num">{rv.normal_count}</span> normal
            </p>
          </div>

          {!showRevenge ? (
            <p
              data-testid="revenge-locked"
              className="text-sm text-[var(--bt-text-muted)] py-2"
            >
              Log more trades to unlock your revenge-trading insight (need at least 5 trades including 1+ loss followed by another trade).
            </p>
          ) : (
            <>
              <div className="grid grid-cols-3 gap-3 sm:gap-4">
                <div>
                  <p className="bt-label text-[10px]">After-Loss Win %</p>
                  <p
                    data-testid="revenge-win-rate"
                    className="bt-num text-3xl mt-1"
                    style={{ color: revengeFlagged ? "var(--bt-loss)" : "var(--bt-win)" }}
                  >
                    {rv.revenge_win_rate.toFixed(2)}%
                  </p>
                  <p className="text-[11px] text-[var(--bt-text-muted)] mt-0.5">
                    <span className="bt-num">{rv.revenge_wins}</span>/
                    <span className="bt-num">{rv.revenge_count}</span> wins
                  </p>
                </div>
                <div>
                  <p className="bt-label text-[10px]">Normal Win %</p>
                  <p
                    data-testid="revenge-normal-rate"
                    className="bt-num text-3xl mt-1"
                    style={{ color: "var(--bt-text)" }}
                  >
                    {rv.normal_win_rate.toFixed(2)}%
                  </p>
                  <p className="text-[11px] text-[var(--bt-text-muted)] mt-0.5">
                    <span className="bt-num">{rv.normal_wins}</span>/
                    <span className="bt-num">{rv.normal_count}</span> wins
                  </p>
                </div>
                <div>
                  <p className="bt-label text-[10px]">Emotional Tags</p>
                  {emotionGap ? (
                    <>
                      <p
                        data-testid="revenge-emotional-unavailable"
                        className="text-sm font-medium mt-1"
                        style={{ color: "var(--bt-text-muted)" }}
                      >
                        Not logged
                      </p>
                      <p className="text-[11px] text-[var(--bt-text-muted)] mt-0.5 leading-tight">
                        Log emotion on trades to unlock this
                      </p>
                    </>
                  ) : (
                    <>
                      <p
                        data-testid="revenge-emotional"
                        className="bt-num text-3xl mt-1"
                        style={{
                          color: rv.revenge_emotional > 0 ? "var(--bt-loss)" : "var(--bt-text)",
                        }}
                      >
                        {rv.revenge_emotional}
                      </p>
                      <p className="text-[11px] text-[var(--bt-text-muted)] mt-0.5">
                        Revenge · FOMO · Greedy
                      </p>
                    </>
                  )}
                </div>
              </div>

              <div
                data-testid="revenge-insight"
                className="rounded-md p-4 mt-2 border"
                style={{
                  background: revengeFlagged
                    ? "color-mix(in srgb, var(--bt-loss) 10%, transparent)"
                    : (emotionGap
                        ? "color-mix(in srgb, var(--bt-text-muted) 8%, transparent)"
                        : "color-mix(in srgb, var(--bt-win) 12%, transparent)"),
                  borderColor: revengeFlagged
                    ? "color-mix(in srgb, var(--bt-loss) 50%, transparent)"
                    : (emotionGap
                        ? "var(--bt-border)"
                        : "color-mix(in srgb, var(--bt-win) 50%, transparent)"),
                }}
              >
                <p
                  className="text-sm leading-relaxed"
                  style={{ color: revengeFlagged ? "var(--bt-loss)" : (emotionGap ? "var(--bt-text)" : "var(--bt-win)") }}
                >
                  {revengeFlagged ? (
                    <>
                      <span className="font-semibold">⚠️ Revenge Trading detected</span>
                      {" "}— You took <span className="bt-num font-semibold">{rv.revenge_count}</span>{" "}
                      trade{rv.revenge_count === 1 ? "" : "s"} right after a loss. Their win rate was{" "}
                      <span className="bt-num font-semibold">{rv.revenge_win_rate.toFixed(2)}%</span>{" "}
                      vs <span className="bt-num font-semibold">{rv.normal_win_rate.toFixed(2)}%</span>{" "}
                      normally.
                      {!emotionGap && rv.revenge_emotional > 0 && (
                        <>
                          {" "}
                          <span className="bt-num font-semibold">{rv.revenge_emotional}</span>{" "}
                          tagged Revenge / FOMO / Greedy.
                        </>
                      )}
                      {" "}Trading angry after a loss is costing you. After a loss, take a break before the next trade.
                    </>
                  ) : emotionGap ? (
                    /* Iter 127 — Partial insight: after-loss win rate
                       looks fine but we have no emotion tags to
                       corroborate an "Emotional Control" claim. Be
                       honest — surface the numbers we DO know and
                       point the user at the missing data. */
                    <>
                      <span className="font-semibold">After-loss win rate:{" "}
                        <span className="bt-num">{rv.revenge_win_rate.toFixed(2)}%</span></span>
                      {" "}vs{" "}
                      <span className="bt-num">{rv.normal_win_rate.toFixed(2)}%</span>{" "}
                      normally — no meaningful gap.{" "}
                      Emotion tags aren&apos;t logged on these trades, so we can&apos;t verdict emotional control fully. Tag a few trades with Revenge / FOMO / Greedy after a loss to unlock the full read.
                    </>
                  ) : (
                    <>
                      <span className="font-semibold">✅ Emotional Control</span>
                      {" "}— You don't chase losses. You stay disciplined after a losing trade
                      (<span className="bt-num">{rv.revenge_win_rate.toFixed(2)}%</span>{" "}
                      win rate after losses vs{" "}
                      <span className="bt-num">{rv.normal_win_rate.toFixed(2)}%</span> normally).
                    </>
                  )}
                </p>
              </div>
            </>
          )}
        </section>
      )}

      {/* ─── Iter 31: "Your Edge" — best & worst pattern (side-by-side cards) ─── */}
      {totalTradesCount > 0 && (
        lowSample ? (
          // ── Iter 39: Per-source/symbol/emotion win rates are too noisy
          // below MIN_TRADES_FOR_INSIGHT trades — surface the same
          // "Building your profile" placeholder used by the breakdown cards.
          <section
            data-testid="edge-section"
            className="bt-card p-6 space-y-3"
          >
            <p className="bt-label flex items-center gap-2">
              <span aria-hidden="true">🎯</span> Your Edge
            </p>
            <BuildingProfileNotice
              tradesRemaining={tradesRemaining}
              testId="edge-strength-leak-placeholder"
            />
          </section>
        ) : showEdge ? (
          <div data-testid="edge-section" className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Strength card */}
            <section
              data-testid="edge-best-card"
              className="bt-card p-6 space-y-3"
              style={{
                background: "color-mix(in srgb, var(--bt-win) 8%, var(--bt-surface))",
                borderColor: "color-mix(in srgb, var(--bt-win) 40%, var(--bt-border))",
              }}
            >
              <p className="bt-label flex items-center gap-2">
                <span aria-hidden="true">🏆</span> Your Strength
              </p>
              <div className="flex items-baseline gap-2 flex-wrap">
                <span className="text-xs uppercase tracking-wider text-[var(--bt-text-muted)]">
                  {DIM_LABELS[edge.best.dimension] || edge.best.dimension}
                </span>
                <span className="font-heading text-2xl">{edge.best.label}</span>
              </div>
              <div className="grid grid-cols-3 gap-3 sm:gap-4">
                <div>
                  <p className="bt-label text-[10px]">Win Rate</p>
                  <p
                    data-testid="edge-best-win-rate"
                    className="bt-num text-3xl mt-1"
                    style={{ color: "var(--bt-win)" }}
                  >
                    {edge.best.win_rate.toFixed(2)}%
                  </p>
                </div>
                <div>
                  <p className="bt-label text-[10px]">Trades</p>
                  <p className="bt-num text-3xl mt-1" style={{ color: "var(--bt-text)" }}>
                    {edge.best.count}
                  </p>
                  <p className="text-[11px] text-[var(--bt-text-muted)] mt-0.5">
                    <span className="bt-num">{edge.best.wins}</span> wins
                  </p>
                </div>
                <div>
                  <p className="bt-label text-[10px]">P&L</p>
                  <p
                    className="bt-num text-3xl mt-1"
                    style={{ color: edge.best.pnl_usd >= 0 ? "var(--bt-win)" : "var(--bt-loss)" }}
                  >
                    {formatUsd(edge.best.pnl_usd)}
                  </p>
                </div>
              </div>
              <div
                className="rounded-md p-4 mt-2 border"
                style={{
                  background: "color-mix(in srgb, var(--bt-win) 10%, transparent)",
                  borderColor: "color-mix(in srgb, var(--bt-win) 45%, transparent)",
                }}
              >
                <p className="text-sm leading-relaxed" style={{ color: "var(--bt-win)" }}>
                  <span className="font-semibold">✅ Your Edge: {edge.best.label}</span>
                  {" "}— <span className="bt-num font-semibold">{edge.best.win_rate.toFixed(2)}%</span>{" "}
                  win rate across <span className="bt-num font-semibold">{edge.best.count}</span>{" "}
                  trade{edge.best.count === 1 ? "" : "s"}. Keep doing this.
                </p>
              </div>
            </section>

            {/* Leak card */}
            <section
              data-testid="edge-worst-card"
              className="bt-card p-6 space-y-3"
              style={{
                background: "color-mix(in srgb, var(--bt-loss) 6%, var(--bt-surface))",
                borderColor: "color-mix(in srgb, var(--bt-loss) 40%, var(--bt-border))",
              }}
            >
              <p className="bt-label flex items-center gap-2">
                <span aria-hidden="true">💧</span> Your Leak
              </p>
              <div className="flex items-baseline gap-2 flex-wrap">
                <span className="text-xs uppercase tracking-wider text-[var(--bt-text-muted)]">
                  {DIM_LABELS[edge.worst.dimension] || edge.worst.dimension}
                </span>
                <span className="font-heading text-2xl">{edge.worst.label}</span>
              </div>
              <div className="grid grid-cols-3 gap-3 sm:gap-4">
                <div>
                  <p className="bt-label text-[10px]">Win Rate</p>
                  <p
                    data-testid="edge-worst-win-rate"
                    className="bt-num text-3xl mt-1"
                    style={{ color: "var(--bt-loss)" }}
                  >
                    {edge.worst.win_rate.toFixed(2)}%
                  </p>
                </div>
                <div>
                  <p className="bt-label text-[10px]">Trades</p>
                  <p className="bt-num text-3xl mt-1" style={{ color: "var(--bt-text)" }}>
                    {edge.worst.count}
                  </p>
                  <p className="text-[11px] text-[var(--bt-text-muted)] mt-0.5">
                    <span className="bt-num">{edge.worst.wins}</span> wins
                  </p>
                </div>
                <div>
                  <p className="bt-label text-[10px]">P&L</p>
                  <p
                    className="bt-num text-3xl mt-1"
                    style={{ color: edge.worst.pnl_usd >= 0 ? "var(--bt-win)" : "var(--bt-loss)" }}
                  >
                    {formatUsd(edge.worst.pnl_usd)}
                  </p>
                </div>
              </div>
              <div
                className="rounded-md p-4 mt-2 border"
                style={{
                  background: "color-mix(in srgb, var(--bt-loss) 10%, transparent)",
                  borderColor: "color-mix(in srgb, var(--bt-loss) 45%, transparent)",
                }}
              >
                <p className="text-sm leading-relaxed" style={{ color: "var(--bt-loss)" }}>
                  <span className="font-semibold">⚠️ Watch out: {edge.worst.label}</span>
                  {" "}— <span className="bt-num font-semibold">{edge.worst.win_rate.toFixed(2)}%</span>{" "}
                  win rate across <span className="bt-num font-semibold">{edge.worst.count}</span>{" "}
                  trade{edge.worst.count === 1 ? "" : "s"}. This is costing you.
                </p>
              </div>
            </section>
          </div>
        ) : (
          <section data-testid="edge-section" className="bt-card p-6">
            <p className="bt-label flex items-center gap-2">
              <span aria-hidden="true">🎯</span> Your Edge
            </p>
            <p
              data-testid="edge-locked"
              className="text-sm text-[var(--bt-text-muted)] mt-2"
            >
              Log more varied trades to unlock your edge analysis (need at least 5 trades with enough variety across sources, symbols or emotions).
            </p>
          </section>
        )
      )}

      {/* ─── Iter 13: Premium analytics row ─── */}
      <div
        data-testid="premium-analytics-row"
        className="grid grid-cols-2 lg:grid-cols-4 gap-4"
      >
        {/* Profit Factor */}
        <MetricCard
          testid="metric-profit-factor"
          label={<>PROFIT FACTOR <MetricInfoTooltip title={TIP.profit_factor.title} description={TIP.profit_factor.description} testId="tip-profit-factor-2" /></>}
          icon={Zap}
        >
          {loading ? (
            <span className="text-[#6b7280]">—</span>
          ) : lowSample ? (
            <BuildingProfileNotice
              tradesRemaining={tradesRemaining}
              testId="metric-profit-factor-2-placeholder"
            />
          ) : isPerfect ? (
            <span style={{ color: pfColor }}>Perfect</span>
          ) : profitFactor == null ? (
            <span className="text-[#6b7280]">—</span>
          ) : (
            <span style={{ color: pfColor }}>{profitFactor.toFixed(1)}x</span>
          )}
        </MetricCard>

        {/* Avg Win / Avg Loss */}
        <MetricCard
          testid="metric-avg-win-loss"
          label="AVG WIN / AVG LOSS"
          icon={Scale3D}
        >
          {loading ? (
            <span className="text-[#6b7280]">—</span>
          ) : (
            <div className="flex items-center gap-3" style={{ fontSize: "24px" }}>
              <span
                data-testid="metric-avg-win"
                style={{ color: "#00ff88", fontWeight: 700 }}
              >
                {formatMoney(avgWin, currency)}
              </span>
              <span style={{ color: "#2a2a2a", fontWeight: 300 }}>|</span>
              <span
                data-testid="metric-avg-loss"
                style={{ color: "#ef4444", fontWeight: 700 }}
              >
                {formatMoney(Math.abs(avgLoss), currency)}
              </span>
            </div>
          )}
        </MetricCard>

        {/* Max Drawdown */}
        <MetricCard
          testid="metric-max-drawdown"
          label={<>MAX DRAWDOWN <MetricInfoTooltip title={TIP.max_drawdown.title} description={TIP.max_drawdown.description} testId="tip-max-drawdown" /></>}
          icon={ChevronsDown}
        >
          {loading || maxDD == null ? (
            <span className="text-[#6b7280]">—</span>
          ) : (
            <div style={{ color: "#ef4444" }}>
              <span data-testid="metric-max-drawdown-value">
                {formatMoney(-Math.abs(maxDD), currency)}
              </span>
              {maxDDPct != null && (
                <span className="text-[14px] font-medium ml-2 text-[#6b7280]">
                  ({maxDDPct >= 0 ? "−" : ""}{maxDDPct.toFixed(1)}%)
                </span>
              )}
            </div>
          )}
        </MetricCard>

        {/* Streak */}
        <MetricCard
          testid="metric-streak"
          label={<>BEST / WORST STREAK <MetricInfoTooltip title={TIP.streak.title} description={TIP.streak.description} testId="tip-streak" side="top" /></>}
          icon={Flame}
        >
          {loading ? (
            <span className="text-[#6b7280]">—</span>
          ) : (stats?.total_trades ?? 0) === 0 ? (
            <span className="text-[#6b7280]">—</span>
          ) : (
            <div className="flex items-center gap-3" style={{ fontSize: "24px" }}>
              <span data-testid="metric-best-streak" style={{ color: "#00ff88", fontWeight: 700 }}>
                🔥 {bestStreak}W
              </span>
              <span style={{ color: "#2a2a2a", fontWeight: 300 }}>|</span>
              <span data-testid="metric-worst-streak" style={{ color: "#ef4444", fontWeight: 700 }}>
                💀 {worstStreak}L
              </span>
            </div>
          )}
        </MetricCard>
      </div>

      {/* Chart */}
      <div
        data-testid="pnl-chart-card"
        className="bt-card bg-[var(--bt-surface)] border border-[var(--bt-border)] rounded-sm p-5"
        data-card="true"
      >
        <div className="flex items-start justify-between mb-4">
          <div>
            <p className="bt-label">Performance</p>
            <h2 className="font-heading text-2xl mt-1">Cumulative P&L</h2>
          </div>
          <div className="text-right">
            <p className="bt-label">Currency</p>
            <p className="font-mono text-sm text-[var(--bt-accent)]">
              {currency === "INR" ? `₹ INR · 1 USD = ${stats?.usd_inr_rate ?? 85}` : "$ USD"}
            </p>
          </div>
        </div>

        <div className="h-[340px] -ml-2">
          {chartData.length === 0 ? (
            <div className="h-full flex flex-col items-center justify-center text-center">
              <p className="bt-label">No data yet</p>
              <p className="text-[var(--bt-text-muted)] text-sm mt-2 mb-4">
                Add your first trade to start building your equity curve.
              </p>
              <Link
                to="/trades/new"
                className="px-4 py-2 bg-[var(--bt-accent)] hover:bg-[var(--bt-accent-hover)] text-black font-bold rounded-sm text-sm"
                data-testid="empty-chart-add-btn"
              >
                Add first trade
              </Link>
            </div>
          ) : (
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={chartData} margin={{ top: 10, right: 20, left: 0, bottom: 0 }}>
                <defs>
                  <linearGradient id="pnlGradient" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor={chartC.line} stopOpacity={1} />
                    <stop offset="0%" stopColor={chartC.gradTop} />
                    <stop offset="100%" stopColor={chartC.gradBot} />
                  </linearGradient>
                </defs>
                <CartesianGrid stroke={chartC.grid} strokeDasharray="3 6" vertical={false} />
                <XAxis dataKey="date" stroke={chartC.text} tick={{ fontSize: 11, fill: chartC.text }} tickLine={false} axisLine={{ stroke: chartC.axis }} />
                <YAxis stroke={chartC.text} tick={{ fontSize: 11, fill: chartC.text }} tickLine={false} axisLine={{ stroke: chartC.axis }} width={70}
                  tickFormatter={(v) => (currency === "INR" ? `₹${v}` : `$${v}`)} />
                <Tooltip content={<ChartTooltip currency={currency} theme={theme} />} cursor={{ stroke: chartC.line, strokeOpacity: 0.3 }} />
                <Area
                  type="monotone"
                  dataKey="value"
                  stroke={chartC.line}
                  strokeWidth={2}
                  fill="url(#pnlGradient)"
                  dot={{ r: 3, stroke: chartC.line, fill: chartC.dotFill, strokeWidth: 2 }}
                  activeDot={{ r: 5, fill: chartC.line }}
                />
              </AreaChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>

      {/* Calendar Heatmap */}
      <div
        data-testid="heatmap-card"
        className="bt-card bg-[var(--bt-surface)] border border-[var(--bt-border)] rounded-sm p-5"
        data-card="true"
      >
        <div className="flex items-start justify-between mb-5">
          <div>
            <p className="bt-label">Daily P&L</p>
            <h2 className="font-heading text-2xl mt-1 flex items-center gap-2">
              <CalendarDays className="w-5 h-5 text-[var(--bt-accent)]" />
              Last 12 Weeks
            </h2>
          </div>
          <span className="text-xs text-[var(--bt-text-muted)] font-mono">
            in {currency === "INR" ? "₹ INR" : "$ USD"}
          </span>
        </div>
        <CalendarHeatmap
          dailyMap={dailyMap}
          currency={currency}
        />
      </div>

      {/* Signal vs Self */}
      {stats && (
        <SignalVsSelf
          stats={stats}
          currency={currency}
          lowSample={lowSample}
          tradesRemaining={tradesRemaining}
        />
      )}

      {/* Breakdowns */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <BreakdownWidget
          testid="breakdown-symbol"
          label="By Symbol"
          title="Performance — Symbols"
          items={breakdownItems.symbols}
          currency={currency}
          onItemClick={(symbol) => nav(`/trades?symbol=${encodeURIComponent(symbol)}`)}
          lowSample={lowSample}
          tradesRemaining={tradesRemaining}
        />
        <BreakdownWidget
          testid="breakdown-broker"
          label="By Broker"
          title="Performance — Brokers"
          items={breakdownItems.brokers}
          currency={currency}
          onItemClick={(broker) => nav(`/trades?broker=${encodeURIComponent(broker)}`)}
          lowSample={lowSample}
          tradesRemaining={tradesRemaining}
        />
      </div>

      {/* Iter 51 — Behavioral Insights Drawer (mounted once; opens on InsightTile click). */}
      <BehavioralInsightsDrawer
        open={drawerKey !== null}
        onOpenChange={(o) => { if (!o) setDrawerKey(null); }}
        insightKey={drawerKey}
        stats={stats}
        currency={currency}
        language={language}
      />
    </div>
  );
}
